import { LoadingOutlined } from "@ant-design/icons";
import { Alert, Spin } from "antd";
import { useAppDispatch } from "customHooks/reduxHooks";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import routes from "routes";
import { setInfo } from "store/slices/infoSlice";
import * as yup from "yup";
import { trpc } from "../../../utils/trpc";
import Button from "../../atoms/Button";
import FormHeader from "../../atoms/FormHeader";
import InfoBox from "../../atoms/InfoBox";
import Input from "../../atoms/Input";
import Modal from "../Modal";
import styles from "./UserPersonalInfo.module.scss";
import { analyticsService } from "../../../utils/analyticsService";
import { raiseException } from "../../../utils/raiseException";
import trimString from "../../../utils/trimString";
import { useTherapistData } from '../../../customHooks/therapistDataContext';
import { isDevEnvironment } from '../../../utils/general';
import { createIubendaConsent } from '../../../utils/iubendaAPI';
import logoutCleanup from '../../../utils/logoutCleanup';
import { setFormDirty } from '../../../store/slices/formDirtySlice';

export const UserPersonalInfo = () => {
  const [delMotive, setDelMotive] = useState("");
  const [loadingModalIsOpen, setLoadingModalIsOpen] = useState(false);
  const [modifyPwModalIsOpen, setModifyPwModalIsOpen] = useState(false);
  const [modifyEmailModalIsOpen, setModifyEmailModalIsOpen] = useState(false);
  const [confirmationCodeModalIsOpen, setConfirmationCodeModalIsOpen] = useState(false);
  const [delProfileModalIsOpen, setDelProfileModalIsOpen] = useState(false);
  const [changePwOk, setChangePwOk] = useState<
    "ok" | "notOk" | "notConfirm" | "notOld" | "maxAttempt" | null
  >(null);
  const [changeEmailError, setChangeEmailError] = useState('');
  const [formData, setFormData] = useState<string>('');

  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const dispatch = useAppDispatch();
  const {therapistData} = useTherapistData();
  const navigate = useNavigate();

  const userInfo: any = trpc.mainService.user.getCurrentUserInfo.useQuery({
    therapistId: therapistData?.therapistId || undefined,
  });
  const updateUserInfo = trpc.mainService.patient.updateInfo.useMutation();
  const changePassword = trpc.mainService.user.changePassword.useMutation();
  const changeEmail = trpc.mainService.user.changeEmail.useMutation();
  const deleteAccount = trpc.mainService.patient.deleteAccount.useMutation();
  const deleteNotificationsTokens = trpc.mainService.notificationTokens.deleteNotificationToken.useMutation();
  const resendEmailVerificationCode = trpc.mainService.user.resendEmailVerificationCode.useMutation();
  const setIubendaSubjectId = trpc.authService.auth.setIubendaSubjectId.useMutation();

  const userPreferences = {
    marketing: userInfo.data?.user?.marketing,
    newsletter: userInfo.data?.user?.newsletter,
  };

  const generalPrivacyLink = 'https://mymentis.it/privacy-generale/';

  const emailFormik = useFormik({
    initialValues: {email: ''},
    validationSchema: yup.object().shape({
      email: yup
        .string()
        .trim()
        .matches(
          /^(?!\.)(?!.*\.\.)([A-Z0-9_+-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i,
          "L'email inserita ha un formato non valido"
        )
        .required("L'email è un campo obbligatorio"),
    }),
    onSubmit: (values) => {
      onChangeEmail(values.email);
    },
  });

  const passwordFormik = useFormik({
    initialValues: {
      password: "",
      newPassword: "",
      checkNewPassword: "",
    },
    validationSchema: yup.object().shape({
      password: yup
        .string()
        .required("La password attuale è un campo obbligatorio"),
      newPassword: yup
        .string()
        .matches(
          /^(?=.*[a-z])(?=.*\d)[^\s]{8,}$/,
          "La password non rispetta i criteri di sicurezza"
        )
        .required("La nuova password è un campo obbligatorio"),
      checkNewPassword: yup
        .string()
        .oneOf([yup.ref("newPassword")], "Le due password non combaciano")
        .required("Inserisci di nuovo la nuova password"),
    }),
    onSubmit: (values) => {
      onChangePasword(values.password, values.newPassword);
    },
  });

  const userInfoFormik = useFormik({
    initialValues: {
      givenName: "",
      familyName: "",
      gender: "",
      emailPartner: "",
      parentEmail: "",
      otherParentEmail: "",
      marketing: false,
      newsletter: false,
    },
    validationSchema: yup.object().shape({
      givenName: yup.string().required("Il nome è un campo obbligatorio"),
      familyName: yup.string(),
      gender: yup.string().required("Il genere è un campo obbligatorio"),
      emailPartner: yup
        .string()
        .trim()
        .matches(
          /^(?!\.)(?!.*\.\.)([A-Z0-9_+-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i,
          "L'email inserita ha un formato non valido"
        )
        .nullable(),
      parentEmail: yup
        .string()
        .trim()
        .matches(
          /^(?!\.)(?!.*\.\.)([A-Z0-9_+-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i,
          "L'email inserita ha un formato non valido"
        )
        .nullable(),
      otherParentEmail: yup
        .string()
        .trim()
        .matches(
          /^(?!\.)(?!.*\.\.)([A-Z0-9_+-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i,
          "L'email inserita ha un formato non valido"
        )
        .nullable(),
      marketing: yup.boolean(),
      newsletter: yup.boolean()
    }),
    onSubmit: (values) => {
      onUpdateUserInfo({
        givenName: values.givenName,
        familyName: values.familyName,
        gender: values.gender as 'unknown' | 'male' | 'female' | 'other' | 'noanswer' | undefined,
        partnerEmail: values.emailPartner,
        parentEmail: values.parentEmail,
        otherParentEmail: values.otherParentEmail,
        marketing: values.marketing,
        newsletter: values.newsletter,
      });
    },
  });

  useEffect(() => {
    if (userInfo.data) {
      const validGenders = ['male', 'female', 'other'];
      const gender = validGenders.includes(userInfo.data.user.gender)
        ? userInfo.data.user.gender
        : '';

      const newValues = {
        givenName: userInfo.data.user.given_name || '',
        familyName: userInfo.data.user.family_name || '',
        gender,
        emailPartner: userInfo.data.user.therapyType === 'couple' ? userInfo.data.user.partnerEmail || '' : '',
        parentEmail: userInfo.data.user.therapyType === 'underage' ? userInfo.data.user.parentEmail || '' : '',
        otherParentEmail: userInfo.data.user.therapyType === 'underage' && !userInfo.data.user.soleGuardianship ?
          userInfo.data.user.otherParentEmail || '' :
          '',
        marketing: userInfo.data.user.marketing || false,
        newsletter: userInfo.data.user.newsletter || false,
      };

      userInfoFormik.resetForm({values: newValues});

      if (!userInfo.data.user.otherParentEmail) {
        userInfoFormik.setFieldError('otherParentEmail', 'L\'email dell\'altro genitore è un campo obbligatorio');
        userInfoFormik.setFieldTouched('otherParentEmail', true, false);
      }
    }
  }, [userInfo.data]);

  useEffect(() => {
    if (userInfoFormik.dirty) {
      window.onbeforeunload = () => '';
    } else {
      window.onbeforeunload = null;
    }
    dispatch(setFormDirty(userInfoFormik.dirty));
    return () => {
      window.onbeforeunload = null;
    };
  }, [userInfoFormik.dirty]);

  useEffect(() => {
    analyticsService({
      event: 'virtualPageview',
      pageUrl: '/dashboard/profilo/informazioni-personali',
      pageTitle: 'Patient Personal Profile',
    });
    if (formRef.current?.outerHTML && !formData) {
      setFormData(formRef.current.outerHTML);
    }
  }, []);

  useEffect(() => {
    if (updateUserInfo.status === 'loading') {
      setLoadingModalIsOpen(true);
    } else {
      setLoadingModalIsOpen(false);
    }
  }, [updateUserInfo]);

  const onChangeEmail = (email) => {
    changeEmail
      .mutateAsync({ email: trimString(email) })
      .then(() => {
        setModifyEmailModalIsOpen(false);
        setConfirmationCodeModalIsOpen(true);
      })
      .catch((e) => {
        raiseException(e);
        if (e?.shape?.errorCode === "EmailAlreadyExists") {
          emailFormik.setErrors({
            ...emailFormik.errors,
            email: "L'email inserita è già in uso",
          });
        } else {
          setChangeEmailError(
            "Si è verificato un errore inaspettato. Verifica di essere connesso a internet e riprova."
          );
        }
      });
  };

  const onChangePasword = (oldPassword, password) => {
    changePassword
      .mutateAsync({
        oldPassword: trimString(oldPassword),
        newPassword: trimString(password),
      })
      .then(() => setChangePwOk('ok'))
      .catch((e) => {
        if (e?.shape?.errorCode === "CurrentPasswordWrong") {
          setChangePwOk("notOld");
        } else if (e?.shape?.errorCode === "MaxAttemptsReached") {
          setChangePwOk("maxAttempt");
        } else {
          setChangePwOk("notOk");
        }
        raiseException(e);
        console.error("ERROR", e?.shape?.message);
      });
  };

  const onUpdateUserInfo = ({
    givenName,
    familyName,
    gender,
    partnerEmail,
    parentEmail,
    otherParentEmail,
    marketing,
    newsletter,
  }) => {
    updateUserInfo
      .mutateAsync({
        givenName: trimString(givenName),
        familyName: trimString(familyName),
        gender,
        partnerEmail: trimString(partnerEmail) || null,
        parentEmail: trimString(parentEmail) || null,
        otherParentEmail: trimString(otherParentEmail) || null,
        marketing,
        newsletter,
      })
      .then(async (data: any) => {
        if (!isDevEnvironment() && (userPreferences.marketing !== marketing || userPreferences.newsletter !== newsletter)) {
          const subjectId = await sendConsentToIubenda(
            trimString(userInfo.data.user.email),
            trimString(givenName),
            trimString(familyName)
          );
          if (subjectId) {
            await setIubendaSubjectId.mutateAsync({
              patientId: data.updatedAttributes.userId,
              iubendaSubjectId: subjectId,
            });
          }
        }

        userPreferences.marketing = marketing;
        userPreferences.newsletter = newsletter;
        userInfo.refetch();
        dispatch(
          setInfo({
            variant: "confirm",
            text: "Infomazioni salvate con successo",
          })
        );
      })
      .catch((e) => {
        if (e?.shape?.errorCode === "EmailAlreadyExists") {
          if ((therapistData?.therapyType ?? userInfo?.data?.user?.therapyType) === 'underage') {
            if (e?.shape?.message === "parentEmail") {
              userInfoFormik.setErrors({
                parentEmail: "L'email inserita è già in uso",
              });
            } else if (e?.shape?.message === "otherParentEmail") {
              userInfoFormik.setErrors({
                otherParentEmail: "L'email inserita è già in uso",
              });
            } else if (e?.shape?.message === "sameParentEmails") {
              userInfoFormik.setErrors({
                parentEmail: "L'email del paziente non deve essere la stessa dell'email del genitore",
                otherParentEmail: "L'email del paziente non deve essere la stessa dell'email del genitore"
              });
            }
          } else if ((therapistData?.therapyType ?? userInfo?.data?.user?.therapyType) === 'couple') {
            userInfoFormik.setErrors({
              emailPartner: "L'email inserita è già in uso",
            });
          } else {
            console.error("ERROR", e?.shape?.message);
            raiseException(e);
            dispatch(
              setInfo({
                variant: "error",
                text: "Si è verificato un errore durante il salvataggio delle informazioni",
              })
            );
          }
        } else {
          console.error("ERROR", e.shape.message);
          dispatch(
            setInfo({
              variant: "error",
              text: "Si è verificato un errore durante il salvataggio delle informazioni",
            })
          );
        }
      });
  };

  const sendConsentToIubenda = async (email: string, firstName: string, lastName: string) => {
    const fullName = `${firstName} ${lastName}`.trim();
    const formContent = {
      givenName: fullName,
      email: email,
      terms_conditions: true,
      marketing: userInfoFormik.values.marketing,
      newsletter: userInfoFormik.values.newsletter,
    };

    const proofsContent = Object.fromEntries(
      Object.entries(formContent).filter(([_, value]) => value !== undefined && value !== '')
    );

    const body = {
      subject: {
        email: email,
        full_name: fullName,
        verified: true,
      },
      preferences: {
        terms_conditions: true,
        marketing: userInfoFormik.values.marketing,
        newsletter: userInfoFormik.values.newsletter,
      },
      proofs: [
        {
          content: JSON.stringify(proofsContent),
          form: formData,
        },
      ],
      legal_notices: [
        {
          identifier: "privacy_policy",
          version: '1',
        },
      ],
    };

    return createIubendaConsent(body);
  };

  const closeConfirmationCodeModal = async () => {
    try {
      await userInfo.refetch();
    } catch (e: any) {
      raiseException(e);
    }
    setConfirmationCodeModalIsOpen(false);
  }

  return (
    <div className={styles.userPersonalInfo}>
      {loadingModalIsOpen && (
        <Modal className="loadingModal">
          <Spin
            indicator={
              <LoadingOutlined style={{ fontSize: 24, color: "white" }} spin />
            }
          />
        </Modal>
      )}
      {modifyPwModalIsOpen && (
        <Modal close={() => setModifyPwModalIsOpen(false)}>
          <form
            onSubmit={passwordFormik.handleSubmit}
            className={styles.modifyModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <FormHeader
              closeModal={() => setModifyPwModalIsOpen(false)}
              closeButton
              title="Modifica password"
            />
            <div className={styles.inputWrapper}>
              <Input
                type="password"
                name="password"
                value={passwordFormik.values.password}
                onChange={passwordFormik.handleChange}
                error={
                  (passwordFormik.errors.password &&
                    passwordFormik.touched.password &&
                    passwordFormik.errors.password) as string
                }
                label="Password attuale"
              />
              <Input
                type="password"
                name="newPassword"
                value={passwordFormik.values.newPassword}
                onChange={passwordFormik.handleChange}
                error={
                  (passwordFormik.errors.newPassword &&
                    passwordFormik.touched.newPassword &&
                    passwordFormik.errors.newPassword) as string
                }
                label="Nuova password"
              />
              <Input
                type="password"
                name="checkNewPassword"
                value={passwordFormik.values.checkNewPassword}
                onChange={passwordFormik.handleChange}
                error={
                  (passwordFormik.errors.checkNewPassword &&
                    passwordFormik.touched.checkNewPassword &&
                    passwordFormik.errors.checkNewPassword) as string
                }
                label="Ripeti nuova password"
              />
            </div>
            {changePwOk === "ok" && (
              <InfoBox
                className={styles.infoBoxModal}
                text={"Password aggiornata correttamente!"}
                variant="confirm"
              />
            )}
            {changePwOk === "notOk" && (
              <InfoBox
                className={styles.infoBoxModal}
                text={"Errore nell'aggiornamento password!"}
                variant="error"
              />
            )}
            {changePwOk === "notOld" && (
              <InfoBox
                className={styles.infoBoxModal}
                text={"La password attuale non è corretta"}
                variant="error"
              />
            )}
            {changePwOk === "maxAttempt" && (
              <InfoBox
                className={styles.infoBoxModal}
                text={"Tentativi massimi raggiunti. Riprova più tardi"}
                variant="error"
              />
            )}
            {changePwOk !== 'ok' && <Button type="submit">Aggiorna password</Button>}
            {changePwOk === "ok" && (
              <Button
                type="button"
                onClick={() => setModifyPwModalIsOpen(false)}
              >
                Conferma!
              </Button>
            )}
          </form>
        </Modal>
      )}
      {modifyEmailModalIsOpen && (
        <Modal close={() => setModifyEmailModalIsOpen(false)}>
          <form
            onSubmit={emailFormik.handleSubmit}
            className={styles.modifyModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <FormHeader
              closeModal={() => setModifyEmailModalIsOpen(false)}
              closeButton
              title="Modifica email"
            />
            <div className={styles.inputWrapper}>
              <Input disabled value={userInfo.data?.user.email || ''} label="Email attuale"/>
              <Input
                type="email"
                name="email"
                value={emailFormik.values.email}
                onChange={emailFormik.handleChange}
                error={
                  (emailFormik.errors.email &&
                    emailFormik.touched.email &&
                    emailFormik.errors.email) as string
                }
                label="Nuova email"
              />
            </div>
            {changeEmailError && (
              <Alert
                closeIcon={null}
                type="error"
                message={changeEmailError}
                closable
              />
            )}
            <InfoBox
              className={styles.infoBoxModal}
              text={
                "Invieremo un’email con un link di conferma sul nuovo indirizzo email che hai inserito."
              }
            />
            <Button type="submit">Invia email di conferma</Button>
          </form>
        </Modal>
      )}
      {confirmationCodeModalIsOpen && (
        <Modal close={closeConfirmationCodeModal}>
          <div className={styles.modifyModalContent} onClick={(e) => e.stopPropagation()}>
            <FormHeader
              closeModal={closeConfirmationCodeModal}
              closeButton
              title="Conferma la tua mail"
            />
            <div className={styles.modalDescription}>
              Abbiamo inviato una mail a <span>{emailFormik.values.email}</span>. Apri il link che trovi al suo interno
              per aggiornare l'email.
            </div>
            <Button
              onClick={async () => {
                await resendEmailVerificationCode.mutateAsync().catch((e) => {
                  raiseException(e);
                  console.error(e);
                });
              }}
            >
              Non hai ricevuto l'email? Rinviala
            </Button>
          </div>
        </Modal>
      )}
      {delProfileModalIsOpen && (
        <Modal close={() => setDelProfileModalIsOpen(false)}>
          <div className={styles.modifyModalContent} onClick={(e) => e.stopPropagation()}>
            <FormHeader
              closeModal={() => setDelProfileModalIsOpen(false)}
              closeButton
              title="Vuoi davvero eliminare il profilo?"
            />
            <div className={styles.modalDescription}>
              Cancellando il profilo non potrai più accedere all’app e al sito.
              Se hai già pagato una seduta che si terrà entro le prossime 24 ore
              non potremo rimborsartela.
            </div>
            <div className={styles.motiveWrapper}>
              <div
                className={`${styles.delMotive} ${delMotive === 'Motivi economici' && styles.active}`}
                onClick={() => setDelMotive("Motivi economici")}
              >
                Motivi economici
              </div>
              <div
                className={`${styles.delMotive} ${delMotive === 'Preferisco la terapia dal vivo' && styles.active}`}
                onClick={() => setDelMotive("Preferisco la terapia dal vivo")}
              >
                Preferisco la terapia dal vivo
              </div>
              <div
                className={`${styles.delMotive} ${delMotive === 'Mi sono trovato male con il mio psicologo Mymentis' && styles.active}`}
                onClick={() => setDelMotive('Mi sono trovato male con il mio psicologo Mymentis')}
              >
                Mi sono trovato male con il mio psicologo Mymentis
              </div>
              <div
                className={`${styles.delMotive} ${delMotive === 'Altro' && styles.active}`}
                onClick={() => setDelMotive("Altro")}
              >
                Altro
              </div>
            </div>
            <div className={styles.btnWrapper}>
              <Button
                variant="secondary"
                onClick={() => {
                  setDelMotive("");
                  setDelProfileModalIsOpen(false);
                }}
              >
                No, ci penso su
              </Button>
              <Button
                disabled={!delMotive}
                onClick={async () => {
                  await deleteAccount.mutateAsync({ reason: delMotive });
                  await deleteNotificationsTokens.mutateAsync();
                  logoutCleanup();
                  navigate(routes.Home.path);
                }}
              >
                Sì, voglio eliminarlo
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <h1 className={styles.title}>Informazioni personali</h1>
      <div className={styles.infoWrapper}>
        <form ref={formRef} className={styles.topPart} onSubmit={userInfoFormik.handleSubmit}>
          <div className={styles.userName}>
            <Input
              label="Nome"
              name="givenName"
              value={userInfoFormik.values.givenName}
              onChange={userInfoFormik.handleChange}
              error={
                (userInfoFormik.errors.givenName &&
                  userInfoFormik.touched.givenName &&
                  userInfoFormik.errors.givenName) as string
              }
            />
            <Input
              label="Cognome"
              name="familyName"
              value={userInfoFormik.values.familyName}
              onChange={userInfoFormik.handleChange}
              error={
                (userInfoFormik.errors.familyName &&
                  userInfoFormik.touched.familyName &&
                  userInfoFormik.errors.familyName) as string
              }
            />
          </div>
          <div className={styles.splitInput}>
            <Input
              label="Genere"
              select
              options={[
                {name: 'Uomo', value: 'male'},
                {name: 'Donna', value: 'female'},
                {name: 'Altro', value: 'other'},
              ]}
              name="gender"
              value={userInfoFormik.values.gender}
              onChange={(e) => {
                userInfoFormik.setFieldValue('gender', e.target.value);
              }}
              error={
                (userInfoFormik.errors.gender &&
                  userInfoFormik.touched.gender &&
                  userInfoFormik.errors.gender) as string
              }
            />
            {(therapistData?.therapyType ?? userInfo?.data?.user?.therapyType) === 'couple' && (
              <Input
                label="Email partner"
                name="emailPartner"
                type="email"
                value={userInfoFormik.values.emailPartner}
                onChange={userInfoFormik.handleChange}
                error={
                  (userInfoFormik.errors.emailPartner &&
                    userInfoFormik.touched.emailPartner &&
                    userInfoFormik.errors.emailPartner) as string
                }
              />
            )}
            {(therapistData?.therapyType ?? userInfo?.data?.user?.therapyType) === 'underage' && userInfo?.data?.user?.soleGuardianship && (
              <Input
                label="Email Genitore 1 / tutore 1 / curatore legale 1"
                name="parentEmail"
                placeholder="Email"
                type="email"
                value={userInfoFormik.values.parentEmail}
                onChange={userInfoFormik.handleChange}
                error={
                  (userInfoFormik.errors.parentEmail &&
                    userInfoFormik.touched.parentEmail &&
                    userInfoFormik.errors.parentEmail) as string
                }
              />
            )}
          </div>
          {(therapistData?.therapyType ?? userInfo?.data?.user?.therapyType) === 'underage' && !userInfo?.data?.user?.soleGuardianship && (
            <div className={styles.splitInput}>
              <Input
                label="Email Genitore 1 / tutore 1 / curatore legale 1"
                name="parentEmail"
                placeholder="Email"
                type="email"
                value={userInfoFormik.values.parentEmail}
                onChange={userInfoFormik.handleChange}
                error={
                  (userInfoFormik.errors.parentEmail &&
                    userInfoFormik.touched.parentEmail &&
                    userInfoFormik.errors.parentEmail) as string
                }
              />
              <Input
                label="Email Genitore 2 / tutore 2 / curatore legale 2"
                name="otherParentEmail"
                placeholder="Email"
                type="email"
                value={userInfoFormik.values.otherParentEmail}
                onChange={userInfoFormik.handleChange}
                error={
                  (userInfoFormik.errors.otherParentEmail &&
                    userInfoFormik.touched.otherParentEmail &&
                    userInfoFormik.errors.otherParentEmail) as string
                }
              />
            </div>
          )}
          <div className={`${styles.checkboxWrapper} ${styles.hiddenButton}`}>
            <Input
              className={styles.checkbox}
              type="checkbox"
              name="marketing"
              checkboxId="marketing"
              checkboxLabel={
                <div>
                  Vorrei ricevere comunicazioni promozionali ai sensi del punto 3.11 della{" "}
                  <span
                    onClick={() => window.open(`${generalPrivacyLink}#311`, '_blank')}
                    style={{
                      color: "#2C2C4B",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Privacy Policy
                  </span>
                </div>
              }
            />
            <Input
              className={styles.checkbox}
              type="checkbox"
              checkboxId="newsletter"
              name="newsletter"
              checkboxLabel={
                <div>
                  Vorrei ricevere newsletter e comunicazioni informative ai sensi del punto 3.10 della{" "}
                  <span
                    onClick={() => window.open(`${generalPrivacyLink}#310`, '_blank')}
                    style={{
                      color: "#2C2C4B",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Privacy Policy
                  </span>
                </div>
              }
            />
          </div>
          <button
            className={styles.hiddenButton}
            ref={submitButtonRef}
            type="submit"
          ></button>
        </form>
        <div className={styles.bottomPart}>
          <div className={styles.inputContainer}>
            <div className={styles.inputName}>Email</div>
            <div className={styles.inputToModify}>
              <Input
                label="Email attuale"
                disabled
                value={userInfo.data?.user.email || ''}
              />
              <Button
                variant="tertiary"
                onClick={() => setModifyEmailModalIsOpen(true)}
              >
                Modifica
              </Button>
            </div>
          </div>
          <div className={styles.inputContainer}>
            <div className={styles.inputName}>Password</div>
            <div className={styles.inputToModify}>
              <Input
                label="Password attuale"
                disabled
                type="password"
                value={"**************"}
              />
              <Button
                variant="tertiary"
                onClick={() => setModifyPwModalIsOpen(true)}
              >
                Modifica
              </Button>
            </div>
          </div>
          <div className={styles.checkboxWrapper}>
            <Input
              checked={userInfoFormik.values.marketing}
              className={styles.checkbox}
              onChange={(e) => {
                userInfoFormik.setFieldValue("marketing", e.target.checked);
              }}
              type="checkbox"
              name="marketing"
              checkboxId="marketing"
              checkboxLabel={
                <div>
                  Vorrei ricevere comunicazioni promozionali ai sensi del punto 3.11 della{" "}
                  <span
                    onClick={() => window.open(`${generalPrivacyLink}#311`, '_blank')}
                    style={{
                      color: "#2C2C4B",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Privacy Policy
                  </span>
                </div>
              }
            />
            <Input
              checked={userInfoFormik.values.newsletter}
              className={styles.checkbox}
              onChange={(e) => {
                userInfoFormik.setFieldValue("newsletter", e.target.checked);
              }}
              type="checkbox"
              name="newsletter"
              checkboxId="newsletter"
              checkboxLabel={
                <div>
                  Vorrei ricevere newsletter e comunicazioni informative ai sensi del punto 3.10 della{" "}
                  <span
                    onClick={() => window.open(`${generalPrivacyLink}#310`, '_blank')}
                    style={{
                      color: "#2C2C4B",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Privacy Policy
                  </span>
                </div>
              }
            />
          </div>
          <div className={styles.btnWrapper}>
            <Button
              variant="secondary"
              onClick={() => {
                userInfoFormik.resetForm();
              }}
            >
              Annulla
            </Button>
            <Button
              disabled={!userInfoFormik.dirty}
              onClick={() => {
                submitButtonRef.current?.click();
              }}
            >
              Salva
            </Button>
          </div>
        </div>
        <Button
          className={styles.deleteProfile}
          variant="tertiary"
          onClick={() => setDelProfileModalIsOpen(true)}
        >
          Elimina profilo
        </Button>
      </div>
    </div>
  );
};